'use client';

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { BeatLoader, SectionHeader } from '@/components/ui';
import { IconEdit, IconExpandDiagonal, IconShrinkDiagonal } from '@repo/svgs';
import { useOwner } from '../../context/OwnerProvider';
import { useStore } from '@/providers/StoreProvider';
import { useAuthModal } from '@/hooks/useAuthModal';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createPortal } from 'react-dom';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface OwnerActionBarProps {
  /** Optional className to customize container */
  className?: string;
}

export function OwnerActionBar({ className }: OwnerActionBarProps) {
  const { owner, ownerReviews, isMasked } = useOwner();
  const { openAuthFlow } = useAuthModal();
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const isUpdatingAccess = useStore(state => state.explorer.isUpdatingAccess);
  const [isExpanded, setIsExpanded] = useState(true);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const analytics = useAnalytics();

  const currentPath = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }, [pathname, searchParams]);

  const handleLogin = () => {
    void openAuthFlow();
  };

  const handleReview = () => {
    analytics.track('contact_review_prompt', { contactId: owner?.id, addressSlug: owner?.slug });
    router.push(`/review`);
  };

  if (!isMasked) {
    return null;
  }

  const handlePurchase = () => {
    analytics.track('get_platform_access', { page: 'contact' });
    router.push(`/subscription?returnTo=${encodeURIComponent(currentPath)}&source=owner`);
  };

  const reviewCount = ownerReviews?.count || 0;
  const maskedTitle =
    reviewCount > 0
      ? `${reviewCount} ${reviewCount === 1 ? 'renter' : 'renters'} reviewed this landlord`
      : "What's it like to live in NYC buildings";
  const maskedSubtitle =
    reviewCount > 0
      ? 'Review your building to read their reviews for free, or purchase access to unlock insights.'
      : 'Review your building to read tenant reviews for free, or purchase access to unlock insights.';

  const renderContent = () => {
    return (
      <>
        <div className={cn('flex gap-1 items-center justify-between w-full px-2 md:px-0')}>
          <SectionHeader className="-mx-2" title={maskedTitle} />
          {isExpanded ? (
            <IconShrinkDiagonal
              className="w-4 h-4 icon-primary md:hidden"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          ) : (
            <IconExpandDiagonal
              className="w-4 h-4 icon-primary md:hidden"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
        </div>
        <div className={cn('flex flex-col gap-4 items-start px-2 py-0 w-full')}>
          {isExpanded ? <p className="text-body-2 text-primary w-full">{maskedSubtitle}</p> : null}
          <div className="flex flex-col gap-2 items-start w-full">
            <div className="flex flex-col gap-4 items-start justify-center w-full">
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={handleReview}
                className="gap-2"
              >
                <IconEdit className="h-4 w-4 icon-inverted" />
                Review your building
              </Button>
              <Button variant="secondary" size="md" fullWidth onClick={handlePurchase}>
                Unlock insights
              </Button>
            </div>

            {!isLoggedIn ? (
              <div className={cn('flex gap-2 items-center justify-center w-full')}>
                <p className="text-body-2 text-primary">Already have an account?</p>
                <button
                  onClick={handleLogin}
                  className={cn(
                    'flex gap-1 items-center justify-center px-0 py-2',
                    'text-body-2-high text-accent text-center cursor-pointer',
                    'hover:underline focus-visible:outline-none focus-visible:underline',
                  )}
                >
                  Log in
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      className={cn(
        'bg-primary flex flex-col items-start w-full relative overflow-hidden',
        'shadow-md-reverse md:shadow',
        className,
      )}
    >
      <div className={cn('flex flex-col items-start justify-center pb-4 pt-1 px-4 w-full')}>
        <div className={cn('flex flex-col items-start justify-center px-0 py-2 w-full')}>
          {renderContent()}
        </div>
      </div>

      {isUpdatingAccess &&
        createPortal(
          <div
            className={cn(
              'fixed inset-0 z-50 bg-white/50 flex flex-col gap-2 items-center justify-center',
            )}
          >
            <BeatLoader />
          </div>,
          document.body,
        )}
    </div>
  );
}
